/**
 * ShopForgotPassword.tsx — Request a password reset link
 * URL: /shop/:businessId/forgot-password
 */
import React, { useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import API_URL from '../../config/api';

const ShopForgotPassword: React.FC = () => {
  const { businessId } = useParams<{ businessId: string }>();

  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [sent, setSent] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      const res = await fetch(`${API_URL}/shop-auth/forgot-password`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ businessId, email }),
      });
      const data = await res.json();
      if (!res.ok) { setError(data.error || 'Request failed'); return; }
      setSent(true);
    } catch {
      setError('Network error. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={{ minHeight: '100vh', background: '#f0f4ff', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', fontFamily: 'system-ui, sans-serif', padding: '24px 16px' }}>
      <div style={{ width: '100%', maxWidth: 420, marginBottom: 12 }}>
        <Link to={`/shop/${businessId}/login`} style={{ fontSize: 13, color: '#6b7280', textDecoration: 'none', display: 'flex', alignItems: 'center', gap: 4 }}>
          ← Back to login
        </Link>
      </div>

      <div style={{ background: '#fff', borderRadius: 16, padding: '40px 36px', maxWidth: 420, width: '100%', boxShadow: '0 4px 24px rgba(0,0,0,0.08)' }}>
        <div style={{ textAlign: 'center', marginBottom: 28 }}>
          <div style={{ fontSize: 44, marginBottom: 8 }}>🔑</div>
          <h1 style={{ margin: 0, fontSize: 22, fontWeight: 800, color: '#111827' }}>Forgot password?</h1>
          <p style={{ margin: '6px 0 0', fontSize: 14, color: '#6b7280' }}>Enter your email and we'll send you a reset link</p>
        </div>

        {sent ? (
          <div style={{ background: '#f0fdf4', border: '1px solid #86efac', borderRadius: 12, padding: '20px 18px', textAlign: 'center' }}>
            <div style={{ fontSize: 36, marginBottom: 10 }}>📧</div>
            <h3 style={{ margin: '0 0 8px', color: '#15803d', fontSize: 16, fontWeight: 700 }}>Check your inbox</h3>
            <p style={{ margin: 0, fontSize: 13, color: '#166534' }}>
              If <strong>{email}</strong> is registered, you'll receive a reset link within a few minutes. Check your spam folder if you don't see it.
            </p>
            <Link
              to={`/shop/${businessId}/login`}
              style={{ display: 'inline-block', marginTop: 18, padding: '10px 24px', borderRadius: 8, background: '#1e3a8a', color: '#fff', fontWeight: 700, fontSize: 14, textDecoration: 'none' }}
            >
              Back to login
            </Link>
          </div>
        ) : (
          <>
            {error && (
              <div style={{ background: '#fef2f2', border: '1px solid #fca5a5', borderRadius: 8, padding: '10px 14px', marginBottom: 18, fontSize: 13, color: '#dc2626', display: 'flex', alignItems: 'center', gap: 8 }}>
                ⚠️ {error}
              </div>
            )}

            <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
              <div>
                <label style={{ display: 'block', fontSize: 13, fontWeight: 600, color: '#374151', marginBottom: 5 }}>Email address</label>
                <input
                  type="email"
                  value={email}
                  onChange={e => setEmail(e.target.value)}
                  placeholder="you@example.com"
                  required
                  autoComplete="email"
                  style={inputStyle}
                />
              </div>

              <button
                type="submit"
                disabled={loading}
                style={{ padding: '13px 0', borderRadius: 9, border: 'none', background: loading ? '#93c5fd' : '#1e3a8a', color: '#fff', fontWeight: 800, fontSize: 15, cursor: loading ? 'not-allowed' : 'pointer', marginTop: 4 }}
              >
                {loading ? 'Sending…' : 'Send Reset Link'}
              </button>
            </form>

            <div style={{ textAlign: 'center', marginTop: 22, fontSize: 13, color: '#6b7280' }}>
              Remembered it?{' '}
              <Link to={`/shop/${businessId}/login`} style={{ color: '#1d4ed8', fontWeight: 700, textDecoration: 'none' }}>
                Sign in
              </Link>
            </div>
          </>
        )}
      </div>

      <p style={{ marginTop: 20, fontSize: 12, color: '#9ca3af' }}>
        Your data is safe and secure 🔒
      </p>
    </div>
  );
};

const inputStyle: React.CSSProperties = {
  width: '100%',
  padding: '11px 14px',
  borderRadius: 8,
  border: '1.5px solid #e5e7eb',
  fontSize: 14,
  outline: 'none',
  boxSizing: 'border-box',
  background: '#fafafa',
};

export default ShopForgotPassword;
